import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useRole } from '@/hooks/queries/useRoles'
import { useUpdateRole } from '@/hooks/mutations/roles'
import { usePermissions } from '@/hooks/queries/usePermissions'
import { toast } from 'sonner'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'

// Function to format permission titles
const formatPermissionTitle = (permission) => {
  return permission
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Validation schema for Formik
const validationSchema = Yup.object({
  title: Yup.string()
    .required('Role title is required')
    .min(3, 'Role title must be at least 3 characters')
    .max(100, 'Role title can\'t be longer than 100 characters'),
  permissions: Yup.array()
    .min(1, 'At least one permission must be selected')
    .required('Permissions are required')
})

const EditRole = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: role, isLoading: isRoleLoading } = useRole(id)
  const { data: permissions, isLoading: isPermissionsLoading } = usePermissions()
  const updateRole = useUpdateRole()

  const [initialValues, setInitialValues] = useState({
    title: '',
    permissions: []
  })

  useEffect(() => {
    if (role) {
      setInitialValues({
        title: role.data.title,
        permissions: Array.isArray(role.data.permissions) ? role.data.permissions.map(p => p.id) : []
      })
    }
  }, [role])

  const handleSubmit = async (values) => {
    try {
      await updateRole.mutateAsync({
        id,
        data: values
      })
      toast.success('Role updated successfully')
      navigate('/roles')
    } catch (error) {
      toast.error(error.message || 'Failed to update role')
    }
  }

  if (isRoleLoading || isPermissionsLoading) return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>
  )

  return (
    <section className='bg-gray-2 dark:bg-dark py-5'>
      <div className='container mx-auto'>
        <div className='flex flex-wrap -mx-4'>
          <div className='w-full px-4'>
            <div className='relative mx-auto max-w-[800px] overflow-hidden rounded-lg bg-white dark:bg-dark-2 shadow-card'>
              <div className='p-8 sm:p-12'>
                <h2 className='mb-6 text-2xl font-bold text-black dark:text-white'>
                  Edit Role
                </h2>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ setFieldValue, values, errors, touched }) => (
                    <Form>
                      <div className='mb-6'>
                        <label className='mb-3 block text-sm font-medium text-dark dark:text-white'>
                          Role Title
                        </label>
                        <Field
                          type='text'
                          name='title'
                          placeholder='Enter role title'
                          className='w-full rounded-md border border-stroke dark:border-dark-3 bg-transparent py-3 px-6 text-base text-body-color dark:text-dark-6 placeholder:text-body-color outline-none transition focus:border-primary focus-visible:shadow-none'
                        />
                        <ErrorMessage name="title" component="div" className="text-red-500 text-sm" />
                      </div>

                      <div className='mb-6'>
                        <label className='mb-3 block text-sm font-medium text-dark dark:text-white'>
                          Permissions
                        </label>
                        <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                          {Array.isArray(permissions?.data) ? (
                            permissions.data.map((permission) => (
                              <div key={permission.id} className='flex items-center'>
                                <Field
                                  type='checkbox'
                                  name='permissions'
                                  value={permission.id}
                                  checked={values.permissions.includes(permission.id)}
                                  onChange={() => {
                                    const selectedPermissions = [...values.permissions]
                                    if (selectedPermissions.includes(permission.id)) {
                                      const index = selectedPermissions.indexOf(permission.id)
                                      selectedPermissions.splice(index, 1)
                                    } else {
                                      selectedPermissions.push(permission.id)
                                    }
                                    setFieldValue('permissions', selectedPermissions)
                                  }}
                                  className='mr-2'
                                />
                                <label 
                                  htmlFor={`permission-${permission.id}`}
                                  className='text-sm text-body-color dark:text-dark-6'
                                >
                                  {formatPermissionTitle(permission.title)}
                                </label>
                              </div>
                            ))
                          ) : (
                            <div>No permissions available</div>
                          )}
                        </div>
                        <ErrorMessage name="permissions" component="div" className="text-red-500 text-sm" />
                      </div>

                      <div className='flex gap-4'>
                        <button
                          type='submit'
                          className='rounded-md bg-primary py-3 px-9 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-opacity-80'
                        >
                          Update Role
                        </button>
                        <button
                          type='button'
                          onClick={() => navigate('/roles')}
                          className='rounded-md bg-gray-2 dark:bg-dark-3 py-3 px-9 text-base font-medium text-body-color dark:text-dark-6 transition duration-300 ease-in-out hover:bg-opacity-80'
                        >
                          Cancel
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default EditRole
